import { Status, StatusCode } from "../helpers/Status";
import { PermissionDeniedReason } from "../types/helpers/permissions";
import { ProblemWithDetail } from "./Problem";
import { ProblemCode } from "./ProblemCode";
import { ExceededRecordLimitData } from "./problems/ExceededRecordLimitProblem";
import { InvalidCustomFileTemplateData } from "./problems/InvalidCustomFileTemplateData";
import { ResourceLockedData } from "./problems/ResourceLockedData";
import { ResourceNotFoundData } from "./problems/ResourceNotFoundProblem";
import { RolePermissionDeniedData } from "./problems/RolePermissionDeniedProblem";
import { TransactionInvalidData } from "./problems/TransactionInvalidData";
import { ValidateKeysFailedData } from "./problems/ValidateKeysFailedData";
import { ValidationData } from "./problems/ValidationProblem";

/**
 * Maps the standard ProblemCode to a `status` and optional default `detail`.
 *
 * The `detail` set here is a fallback, but it's recommended to override to a
 * more specific message at the point of the error being thrown, to provide
 * context of the specific error occurrence.
 *
 * The `detail` is intended for developers using the API, not for the end users
 * of the app.
 */
export const problemMap: Record<
  ProblemCode,
  {
    status: StatusCode;
    detail?: string;
  }
> = {
  [ProblemCode.InternalServerError]: { status: Status.InternalServerError },
  [ProblemCode.AccountFeatureAccessDenied]: {
    status: Status.Forbidden,
    detail: "The Account doesn't have access to the requested Feature.",
  },
  [ProblemCode.UserAccountPermissionDenied]: {
    status: Status.Forbidden,
    detail:
      "User does not have sufficient permission (role) on the account to perform the action.",
  },
  [ProblemCode.ValidationFailed]: {
    status: Status.BadRequest,
    detail: "Invalid request.",
  },
  [ProblemCode.FieldRequired]: {
    status: Status.BadRequest,
    detail: "Missing field.",
  },
  [ProblemCode.FileRequired]: {
    status: Status.BadRequest,
    detail: "Missing file attachment.",
  },
  [ProblemCode.BadRequest]: {
    status: Status.BadRequest,
    detail: "Invalid request.",
  },
  [ProblemCode.ResourceNotFound]: {
    status: Status.NotFound,
    detail: "Resource could not be found.",
  },
  [ProblemCode.TransactionInvalid]: {
    status: Status.BadRequest,
    detail: "The resulting event / transaction failed validation.",
  },
  [ProblemCode.Unauthorised]: {
    status: Status.Unauthorized,
    detail: "Authentication failed.",
  },
  [ProblemCode.Forbidden]: {
    status: Status.Forbidden,
    detail: "You don't have access to do this.",
  },
  [ProblemCode.RefreshedExpiredOtp]: { status: Status.NotFound },
  [ProblemCode.EmailRegistrationExpired]: { status: Status.NotFound },
  [ProblemCode.VerifiedUserAlreadyExists]: { status: Status.NotFound },
  [ProblemCode.GoogleAuthFailed]: { status: Status.Unauthorized },
  [ProblemCode.EmailDisallowed]: {
    status: Status.Forbidden,
    detail: "The email provided is not eligible for an account with Syla.",
  },
  [ProblemCode.UserEmailNotFound]: {
    status: Status.Unauthorized,
    detail: "User does not exist.",
  },
  [ProblemCode.UserHasNoPassword]: {
    status: Status.BadRequest,
    detail: "The user has no current password to check against.",
  },
  [ProblemCode.IncorrectPassword]: {
    status: Status.BadRequest,
    detail: "Password entered is incorrect.",
  },
  [ProblemCode.SamePassword]: {
    status: Status.BadRequest,
    detail: "The new password can't be the same as the old password.",
  },
  [ProblemCode.CannotDeleteLastAccount]: { status: Status.Conflict },
  [ProblemCode.AlreadyInProgress]: { status: Status.Conflict },
  [ProblemCode.ExpiredLink]: { status: Status.NotFound },
  [ProblemCode.InviteExpired]: { status: Status.Unauthorized },
  [ProblemCode.InviteInvalid]: { status: Status.NotFound },
  [ProblemCode.InvitePasswordRequired]: { status: Status.BadRequest },
  [ProblemCode.UserAlreadyHasAccess]: {
    status: Status.BadRequest,
    detail: "User already has access to the account.",
  },
  [ProblemCode.UserInviteNotFound]: {
    status: Status.BadRequest,
    detail: "No user with that email has been invited to the account.",
  },
  [ProblemCode.AccountMustHaveOwner]: {
    status: Status.BadRequest,
    detail: "Account must have at least one Owner user.",
  },
  [ProblemCode.SubscriptionAlreadyExists]: {
    status: Status.Conflict,
    detail: "An active subscription already exists for this account.",
  },
  [ProblemCode.UnsupportedFileType]: {
    status: Status.UnsupportedMediaType,
    detail: "The file provided is not supported.",
  },
  [ProblemCode.ExceededRecordLimit]: {
    status: Status.BadRequest,
  },
  [ProblemCode.FailedFileImport]: {
    status: Status.BadRequest,
  },
  [ProblemCode.InvalidCustomFileTemplate]: {
    status: Status.BadRequest,
  },
  [ProblemCode.ValidateKeysFailed]: {
    status: Status.BadRequest,
  },
  [ProblemCode.ResourceLocked]: {
    status: Status.Locked,
  },
} as const;

export type ProblemStatusFor<C extends ProblemCode> =
  (typeof problemMap)[C]["status"];

export type ProblemWithStatus<C extends ProblemCode> = {
  status: ProblemStatusFor<C>;
};

// if a problem type requires additional data, it can be defined here
export type ProblemDataByCode = {
  [ProblemCode.AccountFeatureAccessDenied]: PermissionDeniedReason;
  [ProblemCode.ValidationFailed]: ValidationData;
  [ProblemCode.UserAccountPermissionDenied]: RolePermissionDeniedData;
  [ProblemCode.ResourceNotFound]: ResourceNotFoundData;
  [ProblemCode.ExceededRecordLimit]: ExceededRecordLimitData;
  [ProblemCode.InvalidCustomFileTemplate]: InvalidCustomFileTemplateData;
  [ProblemCode.ValidateKeysFailed]: ValidateKeysFailedData;
  [ProblemCode.ResourceLocked]: ResourceLockedData;
  [ProblemCode.TransactionInvalid]: TransactionInvalidData;
};

export type ProblemDataFor<C extends ProblemCode> =
  C extends keyof ProblemDataByCode ? ProblemDataByCode[C] : undefined;

export type ProblemWithData<C extends ProblemCode> =
  ProblemDataFor<C> extends undefined ? {} : { data: ProblemDataFor<C> };

export type ProblemWithCode<C extends ProblemCode> = {
  code: C;
};

export type ProblemForCode<C extends ProblemCode> = ProblemWithCode<C> &
  ProblemWithStatus<C> &
  ProblemWithData<C> &
  ProblemWithDetail;
